import { Button } from '@/components/ui/button';
import { Search, MessageCircle, ChevronLeft, ChevronRight, Car, Video, CreditCard, Shield, ShoppingCart, FileText, Wrench, Users, Package, Settings, Gauge, Handshake, HandCoins, TrendingUp, Calendar, Fuel, Palette, Bike, Truck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppData } from '@/contexts/AppDataContext';
import { createPortal } from 'react-dom';
import { generateVehicleSlug } from '@/lib/slug';
import { ServiceModal } from '@/components/ServiceModal';
import { useStore } from '@/contexts/StoreContext';
import { Building2 } from 'lucide-react';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

interface HeroProps {
  config: any;
}

export const Hero = ({ config }: HeroProps) => {
  const { selectedStore, availableStores } = useStore();
  const store = selectedStore || config.stores[0];
  const banners = store.banners?.filter(v => v.platform === 'mobile') || [];
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [nextBannerIndex, setNextBannerIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { vehicles, allVehicles } = useAppData();
  
  // Filter vehicles based on unifiedAds and unifiedAdsStore rules
  const filteredVehicles = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;
    
    // Rule 1: If unifiedAds is false, always filter by selected store
    if (!unifiedAds) {
      if (selectedStoreId) {
        return vehicles.filter(v => v.store === selectedStoreId);
      }
      return vehicles;
    }
    
    // Rule 2: If unifiedAds is true and unifiedAdsStore is null, show all vehicles
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) {
      return vehicles;
    }
    
    // Rule 3 & 4: If unifiedAds is true and unifiedAdsStore is set
    if (selectedStoreId === unifiedAdsStore) {
      // Rule 3: Selected store is the unified store → show all vehicles
      return vehicles;
    } else {
      // Rule 4: Selected store is different → filter by selected store
      if (selectedStoreId) {
        return vehicles.filter(v => v.store === selectedStoreId);
      }
      return vehicles;
    }
  }, [config?.unifiedAds, config?.unifiedAdsStore, vehicles, selectedStore?._id]);

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string; color: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall, color: 'bg-blue-500' },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive, color: 'bg-indigo-500' },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation, color: 'bg-green-500' },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing, color: 'bg-blue-600' },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment, color: 'bg-purple-500' },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium, color: 'bg-teal-500' },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent, color: 'bg-orange-500' },
    '65dcb86a8eca108d87c91dc0': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance, color: 'bg-red-500' },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories, color: 'bg-gray-600' },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices, color: 'bg-indigo-600' },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview, color: 'bg-slate-600' },
    '65dcef498eca108d87c91dc8': { icon: TrendingUp, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable, color: 'bg-cyan-500' },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales, color: 'bg-green-600' }
  };

  const mainServiceInfo = mainServiceId ? serviceInfo[mainServiceId] : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      // Agendar
      '65da42048eca108d87c91da6': 'Agendar', // Agendar Videochamada
      '65dceeac8eca108d87c91dc6': 'Agendar', // Serviços
      '65dcef098eca108d87c91dc7': 'Agendar', // Revisões
      '65dcb5e78eca108d87c91db8': 'Agendar', // Test-Drive

      // Simular
      '65dcb7898eca108d87c91dbc': 'Simular', // Financiamento

      // Avaliar
      '65dcb7548eca108d87c91dbb': 'Avaliar', // Avaliação
      '65dcb7c58eca108d87c91dbd': 'Avaliar', // Consignação

      // Cotar
      '65dcb86a8eca108d87c91dc0': 'Cotar', // Seguros
      '65dcb6528eca108d87c91db9': 'Cotar', // Despachante
      '6628fd535bda2ae51f6fc5ff': 'Cotar', // Peças e Acessórios

      // Solicitar
      '65dcef468eca108d87c91dc8': 'Solicitar', // Consórcio
      '65dcb8548eca108d87c91dbf': 'Solicitar', // Vendas Diretas

      // Consultar
      '65dcef498eca108d87c91dc8': 'Consultar', // Tabela FIPE
    };

    return buttonTexts[serviceId] || 'Solicitar Serviço';
  };

  // Get unique brands (from filtered vehicles)
  const brands = useMemo(() =>
    Array.from(new Set(filteredVehicles.map(v => v.brand))).sort(),
    [filteredVehicles]
  );

  // Generate autocomplete suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];

    const term = searchTerm.toLowerCase();
    const results: Array<{ type: string; text: string; vehicle?: any; filterKey?: string; filterValue?: string; count?: number; minYear?: number; maxYear?: number; storeId?: string }> = [];

    // Suggest stores (show ALL stores from config, including hidden ones)
    // Only show if there are multiple stores
    const allStores = config?.stores || [];
    if (allStores.length > 1) {
      allStores.forEach((store: any) => {
        if (store.company.toLowerCase().includes(term)) {
          // Use allVehicles to count all vehicles from this store
          const count = allVehicles.filter(v => v.store === store._id).length;
          if (count > 0) {
            results.push({ 
              type: 'store', 
              text: store.company, 
              filterKey: 'store', 
              filterValue: store._id,
              storeId: store._id,
              count 
            });
          }
        }
      });
    }

    // Suggest vehicle types (Carro, Moto, Caminhão)
    const vehicleTypes = [
      { text: 'Carro', value: '1' },
      { text: 'Moto', value: '2' },
      { text: 'Caminhão', value: '3' }
    ];
    vehicleTypes.forEach(({ text, value }) => {
      if (text.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.type.toString() === value).length;
        if (count > 0) {
          results.push({ type: 'category', text: text, filterKey: 'category', filterValue: value, count });
        }
      }
    });

    // Suggest brands
    brands.forEach(brand => {
      if (brand.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.brand === brand).length;
        results.push({ type: 'brand', text: brand, count });
      }
    });

    // Suggest models
    const models = Array.from(new Set(filteredVehicles.map(v => `${v.brand} ${v.model}`)));
    models.forEach(model => {
      if (model.toLowerCase().includes(term) && !results.find(s => s.text === model)) {
        const count = filteredVehicles.filter(v => `${v.brand} ${v.model}` === model).length;
        results.push({ type: 'model', text: model, count });
      }
    });

    // Suggest years (count exact, navigate with same year for both min and max)
    const years = Array.from(new Set(filteredVehicles.map(v => v.modelYear))).sort((a, b) => b - a);
    years.forEach(year => {
      if (year.toString().includes(term)) {
        // Count only vehicles with exact modelYear
        const count = filteredVehicles.filter(v => v.modelYear === year).length;

        if (count > 0) {
          // Set both minYear and maxYear to the same year
          const minYear = year;
          const maxYear = year;

          results.push({
            type: 'year',
            text: year.toString(),
            filterKey: 'yearRange',
            filterValue: `${minYear}-${maxYear}`,
            minYear,
            maxYear,
            count
          });
        }
      }
    });

    // Suggest fuels
    const fuels = Array.from(new Set(filteredVehicles.map(v => v.fuel).filter(Boolean)));
    fuels.forEach(fuel => {
      if (fuel.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.fuel === fuel).length;
        results.push({ type: 'fuel', text: fuel, filterKey: 'fuel', filterValue: fuel, count });
      }
    });

    // Suggest transmissions
    const transmissions = Array.from(new Set(filteredVehicles.map(v => v.transmission).filter(Boolean)));
    transmissions.forEach(transmission => {
      if (transmission.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.transmission === transmission).length;
        results.push({ type: 'transmission', text: transmission, filterKey: 'transmission', filterValue: transmission, count });
      }
    });

    // Suggest colors
    const colors = Array.from(new Set(filteredVehicles.map(v => v.color).filter(Boolean)));
    colors.forEach(color => {
      if (color.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.color === color).length;
        results.push({ type: 'color', text: color, filterKey: 'color', filterValue: color, count });
      }
    });

    // Suggest specific vehicles (no count needed)
    filteredVehicles.forEach(vehicle => {
      const fullText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
      if (fullText.includes(term) && results.length < 15) {
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`;
        if (!results.find(s => s.text === vehicleText)) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      }
    });

    return results.slice(0, 10); // Limit to 10 suggestions
  }, [searchTerm, filteredVehicles, brands, allVehicles, config?.stores]);

  // Reset banner index when store changes
  useEffect(() => {
    setCurrentBannerIndex(0);
    setNextBannerIndex(0);
    setIsTransitioning(false);
  }, [store._id]);

  // Auto-rotate banners every 5 seconds when there are multiple banners
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        const nextIndex = (currentBannerIndex + 1) % banners.length;
        setNextBannerIndex(nextIndex);
        setIsTransitioning(true);

        // Após o fade in da próxima imagem (300ms), fazer o fade out da anterior
        setTimeout(() => {
          setCurrentBannerIndex(nextIndex);
          setIsTransitioning(false);
        }, 10); // Duração completa da transição
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [banners.length, currentBannerIndex]);

  const handleBannerChange = (index: number) => {
    if (index !== currentBannerIndex && !isTransitioning) {
      setNextBannerIndex(index);
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentBannerIndex(index);
        setIsTransitioning(false);
      }, 10);
    }
  };

  const currentBanner = banners.length > 0 ? banners[currentBannerIndex] : null;
  const nextBanner = banners.length > 0 ? banners[nextBannerIndex] : null;
  const currentBannerImage = currentBanner ? currentBanner.image : '';
  const nextBannerImage = nextBanner ? nextBanner.image : '';

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/estoque?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      navigate('/estoque');
    }
  };

  const handleBannerClick = async (banner: any) => {
    if (!banner || !banner.type) return;

    switch (banner.type) {
      case 'ad':
        // Check number of values
        if (banner.values && banner.values.length > 0) {
          if (banner.values.length === 1) {
            // Single vehicle - redirect to vehicle detail with slug
            const vehicleId = banner.values[0].value;
            const vehicle = vehicles.find(v => v.id.toString() === vehicleId.toString());
            if (vehicle) {
              const slug = await generateVehicleSlug(vehicle);
              navigate(`/estoque/${slug}`);
            } else {
              navigate(`/estoque/${vehicleId}`); // Fallback to ID
            }
          } else {
            // Multiple vehicles - redirect to banner vehicles page
            const vehicleIds = banner.values.map((v: any) => v.value).join(',');
            const title = encodeURIComponent(banner.title || 'Veículos em Promoção');
            navigate(`/destaques?ids=${vehicleIds}&title=${title}`);
          }
        }
        break;

      case 'link':
        // Open external link
        if (banner.values && banner.values.length > 0 && banner.values[0].value) {
          window.open(banner.values[0].value, '_blank');
        }
        break;

      case null:
      case 'null':
        // No action
        break;

      default:
        break;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0) {
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setShowSuggestions(false);

    if (suggestion.type === 'vehicle' && suggestion.vehicle) {
      // Navigate to vehicle detail with slug (await async function)
      const slug = await generateVehicleSlug(suggestion.vehicle);
      navigate(`/estoque/${slug}`);
    } else if (suggestion.type === 'store' && suggestion.storeId) {
      // Navigate to vehicle listing filtered by store
      navigate(`/estoque?store=${suggestion.storeId}`);
    } else if (suggestion.type === 'year' && suggestion.minYear && suggestion.maxYear) {
      // Navigate with year range
      navigate(`/estoque?minYear=${suggestion.minYear}&maxYear=${suggestion.maxYear}`);
    } else if (suggestion.filterKey && suggestion.filterValue) {
      // Navigate with specific filter
      navigate(`/estoque?${suggestion.filterKey}=${encodeURIComponent(suggestion.filterValue)}`);
    } else {
      // Fill search term and navigate to vehicle listing
      setSearchTerm(suggestion.text);
      navigate(`/estoque?search=${encodeURIComponent(suggestion.text)}`);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      updateDropdownPosition();
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Close suggestions when clicking outside and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        searchRef.current &&
        !searchRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleScroll = (e: Event) => {
      // Não fazer nada se o scroll for dentro do próprio dropdown
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
        return;
      }
      // Atualizar a posição do dropdown ao invés de fechá-lo
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [showSuggestions]);

  return (
    <section
      id="inicio"
      className="relative py-12 md:py-20 pt-[100px] md:pt-[140px] bg-gradient-to-br from-background via-muted/30 to-background"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">

          {/* Hero Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center mt-[50px] md:mt-0 lg:mt-16">

            {/* Search Section - Mobile After Banner, Desktop Left */}
            <div className="lg:col-span-1 lg:order-1 space-y-0 md:space-y-8">

              {/* Main Title - Hidden on Mobile */}
              <div className="hidden lg:block text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl font-bold" style={{ color: titleColor }}>
                  O que você procura?
                </h1>
                <p className="text-lg" style={{ color: textColor }}>
                  Pesquise por marca, modelo, versão, cor e muito mais.
                </p>
              </div>

              {/* Search Bar */}
              <div 
                className={`bg-card/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-1 shadow-elegant border transition-all duration-200 ${
                  isSearchFocused ? 'border-primary' : 'border-border/50'
                }`}
              >
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative" ref={searchRef}>
                    <Input
                      ref={inputRef}
                      placeholder="Digite aqui sua pesquisa..."
                      className="h-10 md:h-12 text-base md:text-lg border-0 bg-background/50 focus-visible:ring-0 focus-visible:ring-offset-0"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={handleKeyPress}
                      onFocus={() => {
                        setIsSearchFocused(true);
                        if (searchTerm.length >= 2) {
                          updateDropdownPosition();
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setIsSearchFocused(false);
                      }}
                    />
                  </div>
                  <Button
                    size="lg"
                    className="hidden md:flex h-10 md:h-12 px-4 md:px-8 text-white hover:shadow-glow transition-all duration-300"
                    style={{
                      background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                    }}
                    onClick={handleSearch}
                  >
                    <Search className="h-5 w-5 mr-2" />
                    <span>Pesquisar</span>
                  </Button>
                </div>
              </div>

              {/* Description - Hidden on Mobile */}
              <p className="hidden lg:block text-sm md:text-xl text-muted-foreground max-w-2xl text-center lg:text-left lg:text-sm" style={{ color: textColor }}>
                Explore nossa seleção de veículos seminovos e zero KM com as melhores condições do mercado e atendimento personalizado.
              </p>
            </div>

            {/* Banners Section - Mobile First, Desktop Right */}
            {banners.length > 0 && (
              <div className="lg:col-span-1 lg:order-2">
                <div className="relative">

                  {/* Banner Display */}
                  <Card className="overflow-hidden shadow-elegant border-border/50 bg-card/80 backdrop-blur-sm rounded-xl md:rounded-2xl">
                    <CardContent
                      className="p-0 relative aspect-[12/7]"
                      style={{
                        cursor: currentBanner?.type && currentBanner.type !== 'none' && currentBanner.type !== null ? 'pointer' : 'default'
                      }}
                      onClick={() => {
                        if (currentBanner?.type && currentBanner.type !== 'null' && currentBanner.type !== null) {
                          handleBannerClick(currentBanner);
                        }
                      }}
                    >
                      {/* Current Banner */}
                      <div
                        className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                        style={{
                          backgroundImage: `url(${currentBannerImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />

                      {/* Next Banner with fade effect */}
                      {isTransitioning && (
                        <div
                          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${isTransitioning ? 'opacity-100' : 'opacity-0'
                            }`}
                          style={{
                            backgroundImage: `url(${nextBannerImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />
                      )}

                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                      {/* Banner Content - Only show for 'ad' type with multiple items */}
                      {currentBanner && currentBanner.type === 'ad' && currentBanner.values && (
                        <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 right-2 md:right-4 text-white z-10">
                          <h3 className="text-sm md:text-lg font-bold mb-1 line-clamp-2">
                            {(currentBanner.title && currentBanner.values.length > 1) ? currentBanner.title : 'Clique para ver o anúncio'}
                          </h3>
                        </div>
                      )}
                      {currentBanner && currentBanner.type === 'link' && currentBanner.values && (
                        <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 right-2 md:right-4 text-white z-10">
                          <h3 className="text-sm md:text-lg font-bold mb-1 line-clamp-2">
                            Clique para visitar o link
                          </h3>
                        </div>
                      )}

                      {/* Navigation Arrows */}
                      {banners.length > 1 && (
                        <>
                          <button
                            className="absolute left-1 md:left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white rounded-full p-1.5 md:p-2 shadow-lg transition-all duration-300 hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              const prevIndex = currentBannerIndex === 0 ? banners.length - 1 : currentBannerIndex - 1;
                              handleBannerChange(prevIndex);
                            }}
                            aria-label="Banner anterior"
                          >
                            <ChevronLeft className="h-4 w-4 md:h-5 md:w-5 text-gray-800" />
                          </button>

                          <button
                            className="absolute right-1 md:right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white rounded-full p-1.5 md:p-2 shadow-lg transition-all duration-300 hover:scale-110"
                            onClick={(e) => {
                              e.stopPropagation();
                              const nextIndex = (currentBannerIndex + 1) % banners.length;
                              handleBannerChange(nextIndex);
                            }}
                            aria-label="Próximo banner"
                          >
                            <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-gray-800" />
                          </button>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Banner Indicators */}
                  {banners.length > 1 && (
                    <div className="flex justify-center gap-1.5 md:gap-2 mt-3 md:mt-4">
                      {banners.map((_, index) => (
                        <button
                          key={index}
                          className={`h-1.5 md:h-2 rounded-full transition-all duration-300 ${index === currentBannerIndex
                            ? 'w-6 md:w-8 bg-accent'
                            : 'w-1.5 md:w-2 bg-border hover:bg-muted-foreground'
                            }`}
                          onClick={() => handleBannerChange(index)}
                          aria-label={`Ir para banner ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Main Service Card - Show when no banners */}
            {banners.length === 0 && mainService && mainServiceInfo && (
              <div className="lg:col-span-1 lg:order-2">
                <Card className="overflow-hidden shadow-elegant border-border/50 bg-card/80 backdrop-blur-sm">
                  {/* Service Image Header */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={mainServiceImage}
                      alt={mainServiceInfo.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                    {/* Icon Overlay */}
                    <div className="absolute top-4 right-4">
                      <div className={`w-14 h-14 ${mainServiceInfo.color} rounded-xl flex items-center justify-center backdrop-blur-sm bg-white/10 shadow-lg`}>
                        <MainServiceIcon className="h-7 w-7 text-white" />
                      </div>
                    </div>

                    {/* Title Overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-2xl leading-tight drop-shadow-lg">
                        {mainServiceInfo.title}
                      </h3>
                    </div>
                  </div>

                  {/* Service Content */}
                  <CardContent className="p-6 space-y-4">
                    <p className="text-muted-foreground leading-relaxed">
                      {mainServiceInfo.description}
                    </p>

                    {/* Action Button */}
                    <ServiceModal service={mainService} config={config}>
                      <Button
                        className="w-full text-white font-semibold py-6"
                        size="lg"
                        style={{
                          background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                        }}
                      >
                        {getServiceButtonText(mainServiceId)}
                      </Button>
                    </ServiceModal>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Autocomplete Suggestions Portal */}
      {showSuggestions && suggestions.length > 0 && dropdownPosition.width > 0 && createPortal(
        <div
          ref={dropdownRef}
          className="absolute bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-2xl max-h-[400px] overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 40,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
              className={`group w-full text-left px-4 py-3 transition-all duration-200 border-b border-border/30 last:border-b-0 first:rounded-t-xl last:rounded-b-xl ${index === selectedSuggestionIndex
                ? 'bg-accent/10 border-l-2 border-l-accent pl-[14px]'
                : 'hover:bg-muted/50 pl-[14px'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {suggestion.type === 'category' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center ring-1 ring-purple-500/20 transition-all duration-200">
                      {suggestion.text === 'Carro' && <Car className="h-4 w-4 text-purple-500" />}
                      {suggestion.text === 'Moto' && <Bike className="h-4 w-4 text-purple-500" />}
                      {suggestion.text === 'Caminhão' && <Truck className="h-4 w-4 text-purple-500" />}
                    </div>
                  )}
                  {suggestion.type === 'store' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600/20 to-blue-600/5 flex items-center justify-center ring-1 ring-blue-600/20 transition-all duration-200">
                      <Building2 className="h-4 w-4 text-blue-600" />
                    </div>
                  )}
                  {suggestion.type === 'brand' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center ring-1 ring-primary/20 transition-all duration-200">
                      <Search className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  {suggestion.type === 'model' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center ring-1 ring-blue-500/20 transition-all duration-200">
                      <Car className="h-4 w-4 text-blue-500" />
                    </div>
                  )}
                  {suggestion.type === 'year' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/20 to-green-500/5 flex items-center justify-center ring-1 ring-green-500/20 transition-all duration-200">
                      <Calendar className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                  {suggestion.type === 'fuel' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-500/5 flex items-center justify-center ring-1 ring-orange-500/20 transition-all duration-200">
                      <Fuel className="h-4 w-4 text-orange-500" />
                    </div>
                  )}
                  {suggestion.type === 'transmission' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/20 to-indigo-500/5 flex items-center justify-center ring-1 ring-indigo-500/20 transition-all duration-200">
                      <Settings className="h-4 w-4 text-indigo-500" />
                    </div>
                  )}
                  {suggestion.type === 'color' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/20 to-pink-500/5 flex items-center justify-center ring-1 ring-pink-500/20 transition-all duration-200">
                      <Palette className="h-4 w-4 text-pink-500" />
                    </div>
                  )}
                  {suggestion.type === 'vehicle' && suggestion.vehicle && (
                    <div className="relative w-14 h-14 rounded-lg overflow-hidden ring-1 ring-border/50 transition-all duration-200">
                      <img
                        src={suggestion.vehicle.photos[0]}
                        alt={suggestion.text}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground truncate group-hover:text-accent-foreground transition-colors">
                        {suggestion.text}
                      </div>

                      <div className="flex items-center gap-2 mt-0.5">
                        {/* Type Badge - Below text */}
                        {suggestion.type === 'category' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-purple-500/10 text-purple-500 rounded-full">
                            Tipo
                          </span>
                        )}
                        {suggestion.type === 'store' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-600/10 text-blue-600 rounded-full">
                            Loja
                          </span>
                        )}
                        {suggestion.type === 'brand' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded-full">
                            Marca
                          </span>
                        )}
                        {suggestion.type === 'model' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-500 rounded-full">
                            Modelo
                          </span>
                        )}
                        {suggestion.type === 'year' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-green-500/10 text-green-500 rounded-full">
                            Ano
                          </span>
                        )}
                        {suggestion.type === 'fuel' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-orange-500/10 text-orange-500 rounded-full">
                            Combustível
                          </span>
                        )}
                        {suggestion.type === 'transmission' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-indigo-500/10 text-indigo-500 rounded-full">
                            Câmbio
                          </span>
                        )}
                        {suggestion.type === 'color' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-pink-500/10 text-pink-500 rounded-full">
                            Cor
                          </span>
                        )}

                        {/* Additional Info */}
                        {suggestion.type === 'vehicle' && suggestion.vehicle && (
                          <>
                            <span className="font-medium">{suggestion.vehicle.modelYear}</span>
                            <span className="text-border">•</span>
                            <span className="font-bold text-foreground">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }).format(suggestion.vehicle.price)}
                            </span>
                          </>
                        )}
                        {/* {suggestion.type === 'year' && suggestion.minYear && suggestion.maxYear && (
                          <span className="text-muted-foreground/70">
                            {suggestion.minYear}-{suggestion.maxYear}
                          </span>
                        )} */}
                      </div>
                    </div>

                    {/* Count Badge - Right Side */}
                    {suggestion.type !== 'vehicle' && suggestion.count !== undefined && (
                      <div className="flex-shrink-0">
                        <span 
                          className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                          {suggestion.count}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </section >
  );
};